import { getCountries, getCountryCallingCode, type CountryCode } from 'libphonenumber-js';

/**
 * Countries, dial codes and flags.
 *
 * The list and the dial codes come from `libphonenumber-js` rather than a table
 * maintained here, because dial plans change and a stale hand-written list is a
 * church that cannot enter a valid number. Names come from `Intl.DisplayNames`, so a
 * console in another language shows countries in that language without Trinity
 * shipping a translation table.
 */

export type { CountryCode };

export const COUNTRY_CODES: readonly CountryCode[] = getCountries();

export const dialCode = (country: CountryCode): string =>
  `+${getCountryCallingCode(country)}`;

/**
 * The flag as a pair of regional indicator symbols, derived from the country code.
 * Renders as a flag wherever the platform has one and as two letters where it does
 * not, which is an acceptable fallback and never a broken image.
 */
export const flagEmoji = (country: CountryCode): string =>
  country
    .toUpperCase()
    .split('')
    .map((letter) => String.fromCodePoint(0x1f1e6 + letter.charCodeAt(0) - 65))
    .join('');

export const countryName = (country: CountryCode, locale: string): string => {
  try {
    const names = new Intl.DisplayNames([locale], { type: 'region' });
    return names.of(country) ?? country;
  } catch {
    return country;
  }
};

/** Ghana first: Trinity's first churches are Ghanaian and it is their default. */
export const DEFAULT_COUNTRY: CountryCode = 'GH';

/** Sorted for display in the resolved locale, not in code order. */
export const sortedCountries = (
  locale: string,
  priority: CountryCode[] = [DEFAULT_COUNTRY],
): { code: CountryCode; name: string; dial: string; flag: string }[] => {
  const collator = new Intl.Collator(locale);

  return [...COUNTRY_CODES]
    .map((code) => ({
      code,
      name: countryName(code, locale),
      dial: dialCode(code),
      flag: flagEmoji(code),
    }))
    .sort((a, b) => {
      const aPriority = priority.indexOf(a.code);
      const bPriority = priority.indexOf(b.code);
      if (aPriority !== bPriority) {
        if (aPriority === -1) return 1;
        if (bPriority === -1) return -1;
        return aPriority - bPriority;
      }
      return collator.compare(a.name, b.name);
    });
};
